import axios from 'axios'

const API_BASE_URL = '/api'

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Don't set Content-Type for FormData, let browser handle it
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // Only clear token and redirect on authentication errors for protected routes
    // Don't redirect guests on public endpoints like order creation
    const isAuthEndpoint = error.config?.url?.includes('/auth/') || 
                          error.config?.url?.includes('/my-orders') ||
                          error.config?.url?.includes('/profile')
    
    // Only redirect to login if:
    // 1. It's a 401 error
    // 2. It's an auth endpoint
    // 3. The error indicates authentication failure (not just invalid data)
    // 4. We're not already on login/register pages
    if (error.response?.status === 401 && isAuthEndpoint &&
        (error.response?.data?.code === 'INVALID_TOKEN' || 
         error.response?.data?.code === 'TOKEN_EXPIRED' ||
         error.response?.data?.message?.includes('token') ||
         error.response?.data?.message?.includes('authentication') ||
         error.response?.data?.message?.includes('unauthorized')) &&
        !window.location.pathname.includes('/login') && 
        !window.location.pathname.includes('/register') &&
        !window.location.pathname.includes('/admin')) {
      
      console.log('[API] Clearing token due to auth error:', error.response?.data)
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    
    // Preserve the error structure for proper error handling
    const errorObj = {
      response: {
        data: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      },
      message: error.message
    }
    
    return Promise.reject(errorObj)
  }
)

// Auth service
export const authService = {
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials)
    return response
  },
  
  register: async (userData) => {
    const response = await api.post('/auth/register', userData)
    return response
  },
  
  googleLogin: async (googleData) => {
    const response = await api.post('/auth/google', googleData)
    return response
  },
  
  getProfile: async () => {
    const response = await api.get('/auth/profile')
    return response.user
  },
  
  logout: async () => {
    const response = await api.post('/auth/logout')
    return response
  },
  
  // verifyEmail: DISABLED - Using code verification only
  // verifyEmail: async (token) => {
  //   const response = await api.post('/auth/verify-email', { token })
  //   return response
  // },
  
  resendVerification: async (email) => {
    const response = await api.post('/auth/resend-verification', { email })
    return response
  },

  verifyEmailCode: async (data) => {
    const response = await api.post('/auth/verify-email-code', data)
    return response
  },

  resendVerificationCode: async (email) => {
    const response = await api.post('/auth/resend-verification-code', { email })
    return response
  },
  
  updateProfile: async (userData) => {
    const response = await api.put('/auth/profile', userData)
    return response
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email })
    return response
  },

  resetPassword: async (token, password) => {
    const response = await api.post('/auth/reset-password', { token, password })
    return response
  },

  verifyResetToken: async (token) => {
    const response = await api.get(`/auth/verify-reset-token/${token}`)
    return response
  },
}

// Product service
export const productService = {
  getProducts: async (params = {}) => {
    const response = await api.get('/products', { params })
    return response
  },
  
  getProduct: async (id) => {
    const response = await api.get(`/products/${id}`)
    return response.product
  },
  
  getFeaturedProducts: async (limit = 8) => {
    const response = await api.get('/products/featured/list', { params: { limit } })
    return response.products
  },
  
  getRelatedProducts: async (id, limit = 6) => {
    const response = await api.get(`/products/${id}/related`, { params: { limit } })
    return response.products
  },
  
  getProductAttributes: async (id) => {
    const response = await api.get(`/products/${id}/attributes`)
    return response.attributes
  },
  
  updateProductAttributes: async (productId, attributeData) => {
    const response = await api.post(`/products/${productId}/attributes`, attributeData)
    return response
  },
  
  createProduct: async (productData) => {
    const response = await api.post('/products', productData)
    return response
  },
  
  updateProduct: async (id, productData) => {
    const response = await api.put(`/products/${id}`, productData)
    return response
  },
  
  deleteProduct: async (id) => {
    const response = await api.delete(`/products/${id}`)
    return response
  },
}

// Category service
export const categoryService = {
  getCategories: async (includeProducts = false) => {
    const response = await api.get(`/categories${includeProducts ? '?include_products=true' : ''}`)
    return response
  },
  
  getCategory: async (id) => {
    const response = await api.get(`/categories/${id}`)
    return response.category
  },
  
  createCategory: async (categoryData) => {
    const response = await api.post('/categories', categoryData)
    return response
  },
  
  updateCategory: async (id, categoryData) => {
    const response = await api.put(`/categories/${id}`, categoryData)
    return response
  },
  
  deleteCategory: async (id) => {
    const response = await api.delete(`/categories/${id}`)
    return response
  },
}

// Cart service
export const cartService = {
  getCart: async () => {
    const response = await api.get('/cart')
    return response.cart
  },
  
  addToCart: async (productId, quantity = 1, selectedAttributes = {}) => {
    const response = await api.post('/cart/add', { 
      product_id: productId, 
      quantity,
      selected_attributes: selectedAttributes
    })
    return response
  },
  
  updateCartItem: async (itemId, quantity) => {
    const response = await api.put(`/cart/update/${itemId}`, { quantity })
    return response
  },
  
  removeFromCart: async (itemId) => {
    const response = await api.delete(`/cart/remove/${itemId}`)
    return response
  },
  
  clearCart: async () => {
    const response = await api.delete('/cart/clear')
    return response
  },
  
  getCartCount: async () => {
    const response = await api.get('/cart/count')
    return response.item_count
  },
}

// Order service
export const orderService = {
  createOrder: async (orderData) => {
    const response = await api.post('/orders', orderData)
    return response
  },
  
  getOrders: async (params = {}) => {
    const response = await api.get('/orders/my-orders', { params })
    return response
  },
  
  getOrder: async (id) => {
    const response = await api.get(`/orders/${id}`)
    return response.order
  },
  
  updateOrderStatus: async (id, status, notes) => {
    const response = await api.put(`/orders/${id}/status`, { status, notes })
    return response
  },
  
  getOrderByNumber: async (orderNumber) => {
    const response = await api.get(`/orders/number/${orderNumber}`)
    return response
  },
  
  deleteOrder: async (id) => {
    const response = await api.delete(`/orders/${id}`)
    return response
  },
  
  getOrderStatuses: async () => {
    const response = await api.get('/orders/statuses')
    return response.statuses
  },
}

// Admin service
export const adminService = {
  getStats: async (forceRefresh = false) => {
    const url = forceRefresh 
      ? `/admin/stats?_t=${Date.now()}` 
      : '/admin/stats'
    const response = await api.get(url)
    return response
  },
  
  getUsers: async () => {
    const response = await api.get('/admin/users')
    return response // This already contains { users: [...], pagination: {...} }
  },
  
  getUser: async (id) => {
    const response = await api.get(`/admin/users/${id}`)
    return response.user
  },
  
  createUser: async (userData) => {
    const response = await api.post('/admin/users', userData)
    return response
  },
  
  updateUser: async (id, userData) => {
    const response = await api.put(`/admin/users/${id}`, userData)
    return response
  },
  
  deleteUser: async (id) => {
    const response = await api.delete(`/admin/users/${id}`)
    return response
  },
  
  getAllOrders: async (params = {}) => {
    const response = await api.get('/orders', { params })
    return response
  },
  
  getOrderDetails: async (orderId) => {
    const response = await api.get(`/orders/${orderId}`)
    return response
  },
}

// Settings service
export const settingsService = {
  getSettings: async () => {
    const response = await api.get('/settings')
    return response
  },
  
  updateSettings: async (settingsData) => {
    const response = await api.post('/settings', settingsData)
    return response
  },
}

// Slider service
export const sliderService = {
  getSlides: async () => {
    const response = await api.get('/slider')
    return response
  },
  
  getSlidesAdmin: async () => {
    const response = await api.get('/slider/admin')
    return response
  },
  
  createSlide: async (slideData) => {
    const response = await api.post('/slider', slideData)
    return response
  },
  
  updateSlide: async (id, slideData) => {
    const response = await api.put(`/slider/${id}`, slideData)
    return response
  },
  
  deleteSlide: async (id) => {
    const response = await api.delete(`/slider/${id}`)
    return response
  },
}



// Simple Attributes service
export const simpleAttributesService = {
  getAttributes: async () => {
    const response = await api.get('/simple-attributes')
    return response
  },
  
  getAttribute: async (attributeId) => {
    const response = await api.get(`/simple-attributes/${attributeId}`)
    return response
  },
  
  getVariations: async (attributeId) => {
    const response = await api.get(`/simple-attributes/${attributeId}/variations`)
    return response
  },
  
  getAttributeVariations: async (attributeId) => {
    const response = await api.get(`/simple-attributes/${attributeId}/variations`)
    return response
  },
  
  getAllVariations: async () => {
    const response = await api.get('/simple-attributes/variations/all')
    return response
  },
  
  createAttribute: async (attributeData) => {
    const response = await api.post('/simple-attributes', attributeData)
    return response
  },
  
  createVariation: async (attributeId, variationData) => {
    const response = await api.post(`/simple-attributes/${attributeId}/variations`, variationData)
    return response
  },
  
  createAttributeVariation: async (attributeId, variationData) => {
    const response = await api.post(`/simple-attributes/${attributeId}/variations`, variationData)
    return response
  },
  
  updateAttribute: async (attributeId, attributeData) => {
    const response = await api.put(`/simple-attributes/${attributeId}`, attributeData)
    return response
  },
  
  updateVariation: async (variationId, variationData) => {
    const response = await api.put(`/simple-attributes/variations/${variationId}`, variationData)
    return response
  },
  
  updateAttributeVariation: async (attributeId, variationId, variationData) => {
    const response = await api.put(`/simple-attributes/${attributeId}/variations/${variationId}`, variationData)
    return response
  },
  
  deleteAttribute: async (attributeId) => {
    const response = await api.delete(`/simple-attributes/${attributeId}`)
    return response
  },
  
  deleteVariation: async (variationId) => {
    const response = await api.delete(`/simple-attributes/variations/${variationId}`)
    return response
  },
  
  deleteAttributeVariation: async (attributeId, variationId) => {
    const response = await api.delete(`/simple-attributes/${attributeId}/variations/${variationId}`)
    return response
  },
}

// Locations service
export const locationsService = {
  getCountries: async () => {
    const response = await api.get('/locations/countries')
    return response
  },
  
  getCities: async (countryId) => {
    if (countryId) {
      const response = await api.get(`/locations/countries/${countryId}/cities`)
      return response
    } else {
      const response = await api.get('/locations/cities')
      return response
    }
  },
  
  getGovernorates: async (countryId) => {
    if (countryId) {
      const response = await api.get(`/locations/countries/${countryId}/governorates`)
      return response
    } else {
      const response = await api.get('/locations/governorates')
      return response
    }
  },

  getCitiesByGovernorate: async (governorateId) => {
    const response = await api.get(`/locations/governorates/${governorateId}/cities`)
    return response
  },
  
  createCity: async (cityData) => {
    const response = await api.post('/locations/cities', cityData)
    return response
  },
  
  updateCity: async (id, cityData) => {
    const response = await api.put(`/locations/cities/${id}`, cityData)
    return response
  },
  
  deleteCity: async (id) => {
    const response = await api.delete(`/locations/cities/${id}`)
    return response
  },
}

// Promo codes service
export const promoCodesService = {
  getPromoCodes: async () => {
    const response = await api.get('/promo-codes');
    const result = response.promo_codes || [];
    return result;
  },
  
  getPromoCode: async (id) => {
    const response = await api.get(`/promo-codes/${id}`)
    return response
  },
  
  applyPromoCode: async (code, orderAmount) => {
    const response = await api.post('/promo-codes/apply', { code, orderAmount })
    return response
  },
  
  createPromoCode: async (promoData) => {
    const response = await api.post('/promo-codes', promoData)
    return response
  },
  
  updatePromoCode: async (id, promoData) => {
    const response = await api.put(`/promo-codes/${id}`, promoData)
    return response
  },
  
  deletePromoCode: async (id) => {
    const response = await api.delete(`/promo-codes/${id}`)
    return response
  },
}

// Users service
export const usersService = {
  getUsers: async (params = {}) => {
    const response = await api.get('/admin/users', { params })
    return response // Return full response like adminService
  },

  getUser: async (id) => {
    const response = await api.get(`/admin/users/${id}`)
    return response.user
  },

  createUser: async (userData) => {
    const response = await api.post('/admin/users', userData)
    return response
  },

  updateUser: async (id, userData) => {
    const response = await api.put(`/admin/users/${id}`, userData)
    return response
  },

  deleteUser: async (id) => {
    const response = await api.delete(`/admin/users/${id}`)
    return response
  },

  updateUserStatus: async (id, isActive) => {
    const response = await api.put(`/admin/users/${id}`, { is_active: isActive })
    return response
  },
}

// Payment service
export const paymentService = {
  createPayment: async (orderData) => {
    const response = await api.post('/payments/create', orderData)
    return response
  },

  checkPaymentStatus: async (paymentId) => {
    const response = await api.get(`/payments/status/${paymentId}`)
    return response
  },
}

export default api
